import React from 'react';
import { T, ROW_COLUMNS, ROW_COLUMNS_SELF, ROW_GAP, NUMERIC } from './orders.theme';
import { COPY, money, formatRowDateTime } from './orders.copy';
import { dayLabel } from './orders.selectors';
import OrderStatusPill, { kindOf } from './OrderStatusPill';
import OrderStatusToggle from './OrderStatusToggle';
import moment from 'moment';
/**
 * The list: one sticky header per business day, then its rows, separated by
 * hairlines rather than gaps.
 *
 * A row can be read and paid. Editing, cancelling and reversing a payment all
 * live in the detail panel, so nothing consequential sits under a mis-click in
 * a list someone is scrolling fast.
 */

function DayHeader({ group, currency }) {
  return (
    <div
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 1,
        // Opaque: rows scroll underneath while this is pinned.
        background: T.surface,
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        padding: '14px 12px 8px',
      }}
    >
      <span style={{ fontSize: 12, fontWeight: 700, color: T.ink }}>
        {dayLabel(group.date)}
      </span>
      <span style={{ fontSize: 11.5, color: T.inkFaint, ...NUMERIC }}>
        {COPY.orders(group.entries.length)} · {money(group.subtotal, currency)}
      </span>
    </div>
  );
}

function OrderRow({
  entry,
  columns,
  selected,
  busy,
  currency,
  showStaff,
  canToggle,
  isLast,
  onSelect,
  onToggleStatus,
}) {
  const [hovered, setHovered] = React.useState(false);

  const background = selected ? T.selectedBg : hovered ? T.hover : 'transparent';

  return (
    <div
      role="row"
      tabIndex={0}
      aria-selected={selected}
      onClick={() => onSelect(entry.id)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect(entry.id);
        }
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'grid',
        gridTemplateColumns: columns,
        alignItems: 'center',
        gap: ROW_GAP,
        height: T.rowHeight,
        padding: '0 12px',
        background,
        borderBottom: isLast ? 'none' : `1px solid ${T.hairline}`,
        cursor: 'pointer',
        outline: 'none',
      }}
    >
      <OrderStatusPill kind={kindOf(entry)} onTinted={selected} />

      {/* An invoice is money owed by a named person, so the row says whose
          book it lands in. Stacked under the number rather than given its own
          column: only invoice rows have one, and an empty column on every cash
          row would cost width the totals need. */}
      <span style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
        <span
          style={{
            fontSize: 13,
            fontWeight: selected ? 700 : 600,
            color: selected ? T.brandInk : T.ink,
            ...NUMERIC,
          }}
        >
          Dalab #{entry.orderNumber}
        </span>
        {entry.customerName ? (
          <span
            title={entry.customerName}
            style={{
              fontSize: 11,
              color: T.inkFaint,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {entry.customerName}
          </span>
        ) : null}
      </span>

      {showStaff && (
        <span
          title={entry.staffName}
          style={{
            fontSize: 12.5,
            color: T.inkSoft,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {entry.staffName || '—'}
        </span>
      )}

      <span style={{ fontSize: 12.5, color: T.inkFaint, ...NUMERIC }}>
        {COPY.items(entry.itemCount)}
      </span>

      <span style={{ fontSize: 12.5, color: T.inkSoft, ...NUMERIC }}>
        {formatRowDateTime(entry.time)}
      </span>
      {/* <span style={{ fontSize: 12.5, color: T.inkSoft, ...NUMERIC }}>
        {moment.utc(entry.time).format("YYYY-MM-DD hh:mm:ss A")}
      </span> */}

      {/* Its own column, blank on most rows — which is what makes the one
          carrying a reduction impossible to miss. */}
      <span
        style={{
          fontSize: 12.5,
          fontWeight: 600,
          color: T.danger,
          textAlign: 'right',
          ...NUMERIC,
        }}
      >
        {entry.discount > 0 ? `−${money(entry.discount, currency)}` : ''}
      </span>

      <span
        style={{
          fontSize: 14,
          fontWeight: 700,
          color: T.ink,
          textAlign: 'right',
          ...NUMERIC,
        }}
      >
        {money(entry.total, currency)}
      </span>

      <OrderStatusToggle
        entry={entry}
        busy={busy}
        disabled={!canToggle}
        onToggle={onToggleStatus}
      />
    </div>
  );
}

export default function OrdersList({
  groups,
  selectedOrderId,
  busyOrderId,
  currency,
  showStaff,
  canToggle,
  onSelect,
  onToggleStatus,
  listRef,
}) {
  const columns = showStaff ? ROW_COLUMNS : ROW_COLUMNS_SELF;

  return (
    <div
      ref={listRef}
      role="table"
      style={{
        flex: 1,
        minWidth: 0,
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: T.radiusPanel,
        padding: '0 0 8px',
        overflowY: 'auto',
      }}
    >
      {groups.map((group, groupIndex) => (
        <div
          key={group.dayKey}
          style={
            groupIndex > 0
              ? { borderTop: `1px solid ${T.border}`, marginTop: 6 }
              : undefined
          }
        >
          <DayHeader group={group} currency={currency} />

          {group.entries.map((entry, index) => (
            <OrderRow
              key={entry.id}
              entry={entry}
              columns={columns}
              selected={entry.id === selectedOrderId}
              busy={entry.id === busyOrderId}
              currency={currency}
              showStaff={showStaff}
              canToggle={canToggle}
              isLast={index === group.entries.length - 1}
              onSelect={onSelect}
              onToggleStatus={onToggleStatus}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
